import * as RadixTooltip from '@radix-ui/react-tooltip'
import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Uitleg bij een element dat op zichzelf al leesbaar is. Een tooltip verduidelijkt
 * dus, maar bevat nooit informatie die alleen daar te vinden is.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  className,
  tapToToggle = true,
}: {
  content: ReactNode
  children: ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  className?: string
  /** Zet op false als het omwikkelde element zelf al een klik-actie heeft. */
  tapToToggle?: boolean
}) {
  // De trigger staat er altijd, ook zonder inhoud. Zou het element bij een lege content
  // uit de Root vallen, dan verandert de boomvorm zodra de content verschijnt en remount
  // React het kind: een invoerveld verliest dan zijn focus midden in een woord.
  //
  // Radix opent standaard op hover/focus; touch-devices kennen geen hover, dus zonder
  // een gecontroleerde open-state is de tooltip daar onbereikbaar. Door open zelf bij te
  // houden en op klik/tap te togglen blijft hover op desktop werken en wordt tikken op
  // mobiel de manier om 'm te tonen en weer te verbergen.
  //
  // Radix' Slot roept bij een klik eerst de onClick van het kind aan en daarna deze
  // toggle. Heeft het kind zelf al een klik-actie (een kopieerknop, een navigatie),
  // dan botst dat: tapToToggle=false laat de tooltip dan puur op hover/focus werken.
  const [open, setOpen] = useState(false)

  return (
    <RadixTooltip.Root
      {...(tapToToggle ? { open, onOpenChange: setOpen } : {})}
      delayDuration={250}
    >
      <RadixTooltip.Trigger
        asChild
        onClick={tapToToggle && content ? () => setOpen((current) => !current) : undefined}
      >
        {children}
      </RadixTooltip.Trigger>
      {content ? (
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side={side}
            sideOffset={6}
            collisionPadding={12}
            className={cn(
              'z-50 max-w-xs rounded-card bg-surface px-3 py-2 text-xs leading-relaxed text-ink-soft shadow-pop animate-fade-in',
              className,
            )}
          >
            {content}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      ) : null}
    </RadixTooltip.Root>
  )
}

export const TooltipProvider = RadixTooltip.Provider
